import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { useEffect, useMemo, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useInstance } from "@/contexts/InstanceContext";
import { useJournalFilterContext } from "@/contexts/JournalFilterContext";
import { useTimezone } from "@/contexts/TimezoneContext";

// ❌ REMOVED: Visibility import (v0.66.0 - all memos are private)

// Extend dayjs with UTC and timezone plugins
dayjs.extend(utc);
dayjs.extend(timezone);

// ❌ REMOVED: getVisibilityName function (v0.66.0 - all memos are private)

const getShortcutId = (name: string): string => {
  const parts = name.split("/");
  return parts.length === 4 ? parts[3] : "";
};

export interface UseMemoFiltersOptions {
  includeShortcuts?: boolean;
  includePinned?: boolean;
  // ❌ REMOVED: visibilities (v0.66.0 - all memos are private)
}

export const useMemoFilters = (options: UseMemoFiltersOptions = {}): string | undefined => {
  // ❌ REMOVED: visibilities from destructuring (v0.66.0 - all memos are private)
  const { includeShortcuts = false, includePinned = false } = options;

  const { shortcuts } = useAuth();
  const { filters, shortcut: currentShortcut } = useJournalFilterContext();
  const { memoRelatedSetting } = useInstance();
  const { timezone } = useTimezone(); // Get user's timezone preference

  const prevFilterResultRef = useRef<string>();
  const filtersString = JSON.stringify(filters);

  // Get selected shortcut if needed
  const selectedShortcut = useMemo(() => {
    if (!includeShortcuts) return undefined;
    return shortcuts.find((shortcut) => getShortcutId(shortcut.name) === currentShortcut);
  }, [includeShortcuts, currentShortcut, shortcuts]);

  // Build filter
  const filter = useMemo(() => {
    const conditions: string[] = [];

    // Add shortcut filter if enabled and selected
    if (includeShortcuts && selectedShortcut?.filter) {
      conditions.push(selectedShortcut.filter);
    }

    // Add active filters from context
    for (const filter of filters) {
      if (filter.factor === "contentSearch") {
        conditions.push(`content.contains("${filter.value}")`);
      } else if (filter.factor === "tagSearch") {
        conditions.push(`tag in ["${filter.value}"]`);
      } else if (filter.factor === "tickerSearch") {
        conditions.push(`ticker in ["${filter.value}"]`);
      } else if (filter.factor === "pinned") {
        if (includePinned) {
          conditions.push(`pinned`);
        }
      } else if (filter.factor === "property.hasLink") {
        conditions.push(`has_link`);
      } else if (filter.factor === "property.hasTaskList") {
        conditions.push(`has_task_list`);
      } else if (filter.factor === "property.hasCode") {
        conditions.push(`has_code`);
      } else if (filter.factor === "displayTime") {
        const displayWithUpdateTime = memoRelatedSetting?.displayWithUpdateTime ?? false;
        const factor = displayWithUpdateTime ? "updated_ts" : "created_ts";

        // Parse the date string in user's timezone and convert to UTC timestamp
        // Example: filter.value = "2026-03-02", timezone = "Europe/Paris"
        // Result: Get start of day in Paris time, then convert to UTC timestamp
        const startOfDayInUserTz = dayjs.tz(filter.value, timezone).startOf("day");
        const startOfDayUtc = startOfDayInUserTz.utc();
        const timestampAfter = startOfDayUtc.unix();

        // Calculate end of day (start of next day)
        const endOfDayUtc = startOfDayInUserTz.add(1, "day").utc();
        const timestampBefore = endOfDayUtc.unix();

        conditions.push(`${factor} >= ${timestampAfter} && ${factor} < ${timestampBefore}`);
      }
    }

    return conditions.length > 0 ? conditions.join(" && ") : undefined;
    // ❌ REMOVED: visibilities from dependency array (v0.66.0 - all memos are private)
  }, [includeShortcuts, includePinned, selectedShortcut, filtersString, memoRelatedSetting, timezone]);

  useEffect(() => {
    if (prevFilterResultRef.current !== filter) {
      console.log(`[useMemoFilters] filter changed:`, {
        from: prevFilterResultRef.current,
        to: filter,
      });
      prevFilterResultRef.current = filter;
    }
  }, [filter]);

  return filter;
};
